import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { api, setToken, getToken } from './api';

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [initialized, setInitialized] = useState(null); // null = unknown, true/false
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const s = await api.authStatus();
            setInitialized(!!s.initialized);
        } catch {
            setInitialized(false);
        }
        const t = getToken();
        if (!t) {
            setUser(null);
            setLoading(false);
            return;
        }
        try {
            const me = await api.me();
            setUser(me);
        } catch {
            setToken(null);
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // If we're returning from Google OAuth (hash has session_id=), skip
        // the /auth/me check entirely. AuthCallback will exchange the
        // session_id and hydrate the context. Without this, /auth/me fires
        // BEFORE the token exists → sets user=null → we bounce the callback
        // to /login prematurely.
        if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
            setLoading(false);
            return;
        }
        refresh();
    }, [refresh]);

    const login = useCallback(async (email, password) => {
        const r = await api.login(email, password);
        setToken(r.token);
        setUser(r.user);
        setInitialized(true);
        return r;
    }, []);

    /**
     * Exchange an Emergent Google Auth session_id (received in the URL
     * fragment after redirect) for our JWT and hydrate the auth context.
     * Called from the AuthCallback surface in App.js.
     */
    const loginWithGoogleSession = useCallback(async (sessionId) => {
        const r = await api.googleSession(sessionId);
        setToken(r.token);
        setUser(r.user);
        setInitialized(true);
        return r;
    }, []);

    const setup = useCallback(async (email, password) => {
        // Legacy single-tenant setup — retained only for older bookmarks.
        // New multi-tenant flow uses register/verify.
        const r = await api.setup(email, password);
        setToken(r.token);
        setUser(r.user);
        setInitialized(true);
        return r; // includes recovery_code
    }, []);

    const registerStart = useCallback(async (email, password) => {
        return await api.registerStart(email, password);
    }, []);

    const registerResend = useCallback(async (email) => {
        return await api.registerResend(email);
    }, []);

    const registerVerify = useCallback(async (email, otp) => {
        const r = await api.registerVerify(email, otp);
        setToken(r.token);
        setUser(r.user);
        setInitialized(true);
        return r; // includes recovery_code + trial_days + grace_days
    }, []);

    // Apply an auth session directly — used by the OTP forgot-password flow
    // which returns a fresh JWT + user in one shot.
    const setSession = useCallback(({ token, user: newUser }) => {
        if (token) setToken(token);
        if (newUser) setUser(newUser);
        setInitialized(true);
    }, []);

    const logout = useCallback(() => {
        setToken(null);
        setUser(null);
    }, []);

    const value = {
        user, initialized, loading, refresh, login, setup,
        registerStart, registerResend, registerVerify, setSession, logout,
        loginWithGoogleSession,
    };
    return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthCtx);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}
